import { CarIcon, CheckIcon, FileCheck2Icon, HomeIcon, Loader2Icon, UserCogIcon } from 'lucide-react'

import { JourneyStage, JourneyView, useTarget } from '../../journey'

import type { VehiclePriceState } from './journey'

/**
 * The profile grid, in the order the real screen renders it.
 *
 * `UserEdit` maps over `Object.values(UserProfileRole)` into a four-column
 * grid, so the enum's order IS the grid's order — which is why the new
 * permission sits beside "Gestionar vehículos" and not among the other
 * Modificar* entries. The mock keeps that adjacency because it is the whole
 * reason someone will find the checkbox.
 */
const PERMISSIONS: { label: string; checked: boolean; isTarget?: boolean; faded?: boolean }[] = [
  { label: 'Gestionar clientes', checked: true },
  { label: 'Gestionar consignaciones', checked: false },
  { label: 'Gestionar plantillas', checked: false },
  { label: 'Gestionar miembros', checked: false },
  { label: 'Gestionar pagarés', checked: false },
  { label: 'Gestionar compras', checked: true },
  { label: 'Gestionar notas de venta', checked: true },
  { label: 'Gestionar liquidaciones', checked: false },
  { label: 'Gestionar vehículos', checked: true },
  { label: 'Modificar precio de vehículos', checked: true, isTarget: true },
  { label: 'Modificar compras', checked: false },
  { label: 'Modificar notas de venta', checked: false },
  { label: 'Modificar liquidaciones', checked: false, faded: true },
  { label: 'Gestionar negocios propios', checked: true, faded: true },
  { label: 'Gestionar sucursales', checked: false, faded: true },
  { label: 'Ver vehículos', checked: true, faded: true },
]

const TEAM = [
  { name: 'Marcela Fuentes', job: 'Administradora', state: 'Activa', primary: false },
  { name: 'Camila Rojas', job: 'Vendedora', state: 'Activa', primary: true },
  { name: 'Diego Salas', job: 'Vendedor', state: 'Activo', primary: false },
  { name: 'Ignacio Bravo', job: 'Jefe de ventas', state: 'Activo', primary: false },
]

interface ViewProps {
  state: VehiclePriceState
}

export const Simulator = ({ state }: ViewProps) => (
  <JourneyStage
    state={state}
    user={{ name: 'Marcela', role: 'Administradora' }}
    nav={[
      { label: 'Inicio', icon: HomeIcon, active: state.navActive === 'inicio' },
      { label: 'Notas de venta', icon: FileCheck2Icon },
      { label: 'Vehículos', icon: CarIcon },
      { label: 'Usuarios', icon: UserCogIcon, target: 'navUsuarios', active: state.navActive === 'usuarios' },
    ]}
  >
    <HomeView />
    <LoadingView />
    <UsersView />
    <PermissionsView state={state} />
  </JourneyStage>
)

const HomeView = () => (
  <JourneyView name="home" className="flex flex-col bg-white p-3">
    <h3 className="text-[10px] font-bold text-[#1c1917]">Hola, Automotora Central</h3>
    <p className="mb-2 text-[7px] text-[#706d71]">Resumen del mes en curso</p>

    <div className="mb-2 grid grid-cols-3 gap-1.5">
      {[
        ['Ventas del mes', '12'],
        ['En stock', '48'],
        ['Reservas', '5'],
      ].map(([label, value]) => (
        <div key={label} className="rounded border border-[#e4e1e4] bg-[#faf8f8] p-1.5">
          <p className="font-mono text-[6px] uppercase text-[#706d71]">{label}</p>
          <p className="mt-0.5 text-[10px] font-bold text-[#1c1917]">{value}</p>
        </div>
      ))}
    </div>

    <div className="flex-1 rounded border border-[#e4e1e4] p-1.5">
      <p className="mb-1.5 text-[7px] font-bold text-[#1c1917]">Actividad reciente</p>
      <div className="space-y-1">
        {['bg-[#107c41]', 'bg-[#e5484d]', 'bg-stone-300'].map((dot, index) => (
          <div key={dot} className="flex items-center gap-1.5">
            <div className={`h-1 w-1 shrink-0 rounded-full ${dot}`} />
            <div className="h-1.5 animate-pulse rounded bg-stone-200" style={{ width: `${90 - index * 18}%` }} />
          </div>
        ))}
      </div>
    </div>
  </JourneyView>
)

const LoadingView = () => (
  <JourneyView name="loading" className="bg-white p-3">
    <div className="mb-2.5 h-2 w-1/3 animate-pulse rounded bg-stone-200" />
    <div className="space-y-1.5">
      {[100, 92, 80, 100, 66].map((width, index) => (
        <div key={index} className="h-1.5 animate-pulse rounded bg-stone-200" style={{ width: `${width}%` }} />
      ))}
    </div>
  </JourneyView>
)

const UsersView = () => {
  const member = useTarget('member')

  return (
    <JourneyView name="users" className="flex flex-col bg-white p-3">
      <h3 className="text-[10px] font-bold text-[#1c1917]">Usuarios</h3>
      <p className="mb-2 text-[7px] text-[#706d71]">Miembros de Automotora Central</p>

      <div className="overflow-hidden rounded border border-[#e4e1e4]">
        <div className="grid grid-cols-[1.4fr_1fr_0.7fr] gap-1 border-b border-[#e4e1e4] bg-[#faf8f8] px-1.5 py-1">
          {['Nombre', 'Cargo', 'Estado'].map((column) => (
            <span key={column} className="font-mono text-[6px] uppercase text-[#706d71]">
              {column}
            </span>
          ))}
        </div>
        {TEAM.map((person) => (
          <div
            key={person.name}
            ref={person.primary ? member : undefined}
            className="grid grid-cols-[1.4fr_1fr_0.7fr] gap-1 border-b border-[#f2eeee] px-1.5 py-1.5 last:border-b-0"
          >
            <span className={`text-[7px] ${person.primary ? 'font-medium text-[#1c1917]' : 'text-[#1c1917]'}`}>
              {person.name}
            </span>
            <span className="text-[7px] text-[#706d71]">{person.job}</span>
            <span className="text-[6px] text-[#107c41]">{person.state}</span>
          </div>
        ))}
      </div>
    </JourneyView>
  )
}

const PermissionsView = ({ state }: ViewProps) => {
  const permissionBox = useTarget('permissionBox')
  const save = useTarget('save')

  return (
    <JourneyView name="permissions" className="flex flex-col bg-white p-3">
      <h3 className="text-[10px] font-bold text-[#1c1917]">Camila Rojas</h3>
      <p className="mb-2 text-[7px] text-[#706d71]">Vendedora · Automotora Central</p>

      <div className="mb-2 grid grid-cols-3 gap-1.5">
        {[
          ['Email', 'camila@automotora.cl'],
          ['Teléfono', '+56 9 5544 1122'],
          ['Cargo', 'Vendedora'],
        ].map(([label, value]) => (
          <div key={label}>
            <p className="mb-0.5 font-mono text-[6px] uppercase text-[#706d71]">{label}</p>
            <div className="truncate rounded border border-[#e4e1e4] px-1.5 py-1 text-[7px] text-[#1c1917]">
              {value}
            </div>
          </div>
        ))}
      </div>

      <p className="mb-1 font-mono text-[6px] uppercase text-[#706d71]">Permisos</p>

      <div className="grid grid-cols-4 gap-x-1 gap-y-0.5">
        {PERMISSIONS.map((permission) => {
          const checked = permission.isTarget ? state.permissionChecked : permission.checked

          return (
            <div
              key={permission.label}
              ref={permission.isTarget ? permissionBox : undefined}
              className={`vk-perm-cell flex items-center gap-1 rounded px-1 py-[3px] ${
                permission.faded ? 'opacity-40' : ''
              }`}
              data-highlighted={permission.isTarget && state.permissionHighlighted}
            >
              <span
                className={`vk-perm-box flex h-2 w-2 shrink-0 items-center justify-center rounded-[2px] border ${
                  checked ? 'border-[#e5484d]' : 'border-stone-300 bg-white'
                }`}
                data-checked={checked}
                data-tone="red"
              >
                {checked && <CheckIcon className="h-1.5 w-1.5 text-white" strokeWidth={4} />}
              </span>
              <span className={`text-[6px] leading-tight ${permission.isTarget ? 'font-medium' : ''}`}>
                {permission.label}
              </span>
            </div>
          )
        })}
      </div>

      <div className="mt-3 flex items-center gap-1.5">
        <span
          ref={save}
          className="flex items-center gap-1 rounded bg-[#e5484d] px-2 py-1 text-[7px] font-bold text-white"
        >
          {state.saving && <Loader2Icon className="h-2 w-2 animate-spin" />}
          <span>Modificar</span>
        </span>
        <span className="rounded border border-[#e4e1e4] px-2 py-1 text-[7px] text-[#706d71]">Cancelar</span>
      </div>

      <div
        className="absolute bottom-2 right-2 z-30 flex items-center gap-1 rounded bg-[#1c1917] px-2 py-1.5 text-[7px] text-white transition-all duration-300"
        style={{
          opacity: state.savedToast ? 1 : 0,
          transform: `translateY(${state.savedToast ? '0' : '0.75rem'})`,
        }}
      >
        <CheckIcon className="h-2.5 w-2.5 text-[#107c41]" />
        <span>Permisos actualizados</span>
      </div>
    </JourneyView>
  )
}
